import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/api/client";
import TopBar from "@/components/TopBar";
import { useCart } from "@/contexts/CartContext";
import MealModal from "@/components/MealModal";
import PushEnableBanner from "@/components/PushEnableBanner";
import useWebSocket from "@/hooks/useWebSocket";
import { Truck, PackageCheck, Search, Store, MapPin, Clock, Phone, ArrowRight, Radio, X } from "lucide-react";

export default function RestaurantMenu() {
  const { slug } = useParams();
  const nav = useNavigate();
  const cart = useCart();

  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [liveTick, setLiveTick] = useState(null); // last live update timestamp
  const restaurantIdRef = useRef(null);

  // Reusable fetcher — loads restaurant + menu + categories
  const load = useCallback(async (opts = {}) => {
    const showLoader = opts.showLoader !== false;
    if (showLoader) setLoading(true);
    setNotFound(false);
    try {
      let r = null;
      try {
        const { data } = await api.get(`/restaurants/by-slug/${slug}`);
        r = data;
      } catch {
        try {
          const { data } = await api.get(`/restaurants/${slug}`);
          r = data;
        } catch { /* still null */ }
      }
      if (!r) { setNotFound(true); return; }
      restaurantIdRef.current = r.id;
      setRestaurant(r);
      // Only prompt about switching restaurants on the FIRST load, not on live refreshes
      if (showLoader && cart.restaurant && cart.restaurant.id !== r.id && cart.items.length > 0) {
        if (window.confirm(`لديك أصناف من "${cart.restaurant.name}" في السلة. سيتم إفراغها للطلب من "${r.name}". هل تريد المتابعة؟`)) {
          cart.clear();
        } else {
          nav(`/r/${cart.restaurant.slug || cart.restaurant.id}`);
          return;
        }
      }
      if (showLoader) cart.setRestaurant(r);
      const [m, c] = await Promise.all([
        api.get(`/menu?restaurant_id=${r.id}`),
        api.get(`/menu/categories?restaurant_id=${r.id}`),
      ]);
      setMenu(m.data); setCats(c.data);
      if (!showLoader) setLiveTick(Date.now());
    } finally { if (showLoader) setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Live sync — any menu / restaurant change from admin or restaurant owner triggers refetch
  useWebSocket({
    onMessage: (msg) => {
      const rid = restaurantIdRef.current;
      if (!rid) return;
      if (msg.event === "menu.changed" && msg.data?.restaurant_id === rid) {
        load({ showLoader: false });
      } else if (msg.event === "restaurant.updated" && msg.data?.id === rid) {
        load({ showLoader: false });
      } else if (msg.event === "restaurant.deleted" && msg.data?.id === rid) {
        setNotFound(true);
      }
    },
  });

  // Arabic-aware normalized search: case-insensitive, ignores hamza / alef variants,
  // diacritics, and taa-marbuta so "شاورما" matches "شَاوَرْمَا", and "بطاطا" matches "بطاطة".
  const normalize = (s) => (s || "")
    .toString()
    .toLowerCase()
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[\u064B-\u0652\u0670]/g, "")
    .trim();

  const nq = normalize(q);
  const searching = nq.length > 0;

  // When a query is present, search ACROSS ALL categories (the chip is ignored) and
  // also match against the category name so typing e.g. "حلويات" surfaces every
  // dessert. Category chip still filters when no query.
  const filtered = menu.filter((m) => {
    if (searching) {
      return normalize(m.name).includes(nq)
          || normalize(m.description).includes(nq)
          || normalize(m.category).includes(nq);
    }
    return activeCat === "all" || m.category === activeCat;
  });

  // Categories that themselves match the query — shown as one-tap shortcuts above results.
  const matchingCats = searching
    ? cats.filter((c) => normalize(c).includes(nq))
    : [];

  if (notFound) {
    return (
      <div className="min-h-screen bg-[var(--background)]">
        <TopBar/>
        <div className="max-w-2xl mx-auto p-12 text-center">
          <Store size={48} className="mx-auto text-[var(--text-secondary)] mb-3"/>
          <h1 className="text-2xl font-black" style={{fontFamily:'Cairo'}}>المطعم غير موجود</h1>
          <p className="text-[var(--text-secondary)] mt-2">الرابط قد يكون معطلاً أو تم إلغاء المطعم</p>
          <Link to="/" className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-primary-brand text-white font-bold">تصفح جميع المطاعم</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar/>
      <PushEnableBanner/>

      {/* Search — placed at the very top so customers can find items immediately without scrolling */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sticky top-0 z-30 bg-[var(--background)] pb-2">
        <div className="relative">
          <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
          <input
            placeholder="ابحث في المنيو والأصناف..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pr-11 pl-11 py-3 rounded-2xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none talabya-shadow-soft"
            data-testid="menu-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[var(--surface-alt)] hover:bg-[var(--border)] flex items-center justify-center transition"
              aria-label="مسح البحث"
              data-testid="menu-search-clear"
            >
              <X size={14} className="text-[var(--text-secondary)]"/>
            </button>
          )}
        </div>
        {searching && matchingCats.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="matching-cats">
            <span className="text-[11px] text-[var(--text-secondary)] font-bold">أصناف مطابقة:</span>
            {matchingCats.map((c) => (
              <button
                key={c}
                onClick={() => { setQ(""); setActiveCat(c); }}
                className="text-[11px] font-black px-2.5 py-1 rounded-full bg-primary-light text-primary-brand hover:bg-primary-brand hover:text-white transition"
                data-testid={`match-cat-${c}`}
              >
                #{c}
              </button>
            ))}
          </div>
        )}
        {searching && (
          <div className="mt-2 text-[11px] text-[var(--text-secondary)]" data-testid="search-count">
            {filtered.length > 0
              ? `${filtered.length} نتيجة${filtered.length > 10 ? "" : ""}`
              : "لم نجد نتائج مطابقة"}
          </div>
        )}
      </div>

      {/* Restaurant hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-primary-brand font-bold mb-3" data-testid="back-to-restaurants">
          <ArrowRight size={14}/> جميع المطاعم
        </Link>
        {restaurant && (
          <div className={`rounded-3xl overflow-hidden talabya-shadow-soft relative ${!restaurant.owner_online ? "ring-2 ring-slate-300" : ""}`}>
            <div className="h-48 sm:h-56 relative">
              {restaurant.image
                ? <img src={restaurant.image} alt={restaurant.name} className={`w-full h-full object-cover ${!restaurant.owner_online ? "grayscale" : ""}`}/>
                : <div className="w-full h-full gradient-warm flex items-center justify-center text-6xl">🍽</div>}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"/>
              <div
                className={`absolute top-4 left-4 backdrop-blur text-white text-xs font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md ${restaurant.owner_online ? "bg-emerald-500/95" : "bg-slate-500/95"}`}
                data-testid="shop-status-badge"
              >
                <span className={`w-2 h-2 rounded-full bg-white ${restaurant.owner_online ? "animate-pulse-dot" : "opacity-70"}`}/>
                {restaurant.owner_online
                  ? "مفتوح الآن"
                  : (restaurant.manual_closed && restaurant.manual_closed_reason
                      ? `مغلق حالياً · ${restaurant.manual_closed_reason}`
                      : "مغلق حالياً")}
              </div>
              <div className="absolute bottom-4 right-4 left-4 text-white">
                <h1 className="text-3xl sm:text-4xl font-black" style={{fontFamily:'Cairo'}}>{restaurant.name}</h1>
                {restaurant.cuisine && <p className="text-white/90 text-sm mt-1">{restaurant.cuisine}</p>}
              </div>
            </div>
            <div className="bg-white p-4 flex flex-wrap gap-4 text-xs text-[var(--text-secondary)]">
              <span className="flex items-center gap-1"><MapPin size={12}/> {restaurant.address}</span>
              {restaurant.hours && <span className="flex items-center gap-1"><Clock size={12}/> {restaurant.hours}</span>}
              {restaurant.phone && <span className="flex items-center gap-1"><Phone size={12}/> {restaurant.phone}</span>}
              {liveTick && (
                <span className="mr-auto flex items-center gap-1 text-green-600 font-bold" data-testid="menu-live-tick" title={`آخر تحديث: ${new Date(liveTick).toLocaleTimeString('ar-LY')}`}>
                  <Radio size={12} className="animate-pulse"/> تحديث لحظي
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fulfillment toggle */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-4">
        <div className="flex gap-2 bg-white p-1.5 rounded-2xl talabya-shadow-soft w-fit">
          <button onClick={() => cart.setFulfillment("delivery")}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition ${cart.fulfillment === "delivery" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)] hover:bg-[var(--surface-alt)]"}`}
            data-testid="ff-delivery">
            <Truck size={16}/> توصيل
          </button>
          <button onClick={() => cart.setFulfillment("pickup")}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition ${cart.fulfillment === "pickup" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)] hover:bg-[var(--surface-alt)]"}`}
            data-testid="ff-pickup">
            <PackageCheck size={16}/> استلام
          </button>
        </div>
      </div>

      {/* Categories — hidden when actively searching (query overrides category filter) */}
      {!searching && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-5 pb-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {["all", ...cats].map((c) => (
              <button key={c} onClick={() => setActiveCat(c)}
                className={`px-4 py-2 rounded-xl whitespace-nowrap text-sm font-bold transition ${activeCat === c ? "bg-primary-brand text-white talabya-shadow-float" : "bg-white border border-[var(--border)] text-[var(--text-primary)] hover:border-primary-brand"}`}
                data-testid={`cat-${c}`}>
                {c === "all" ? "الكل" : c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Menu Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_,i)=><div key={i} className="h-72 rounded-2xl shimmer"/>)}
          </div>
        ) : filtered.length === 0 ? (
          menu.length === 0 ? (
            <div className="bg-white rounded-3xl p-10 sm:p-16 text-center talabya-shadow-soft" data-testid="empty-menu">
              <div className="text-5xl mb-4">🍽</div>
              <div className="text-xl font-bold mb-2" style={{fontFamily:'Cairo'}}>قائمة الطعام قيد الإعداد</div>
              <div className="text-sm text-[var(--text-secondary)]">هذا المطعم لم يقم بإضافة أطباق بعد. تابع لاحقاً!</div>
            </div>
          ) : (
            <div className="text-center py-20 text-[var(--text-secondary)]" data-testid="no-search-results">لا توجد نتائج</div>
          )
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => {
              const shopClosed = restaurant && restaurant.owner_online === false;
              const unavailable = item.available === false || shopClosed;
              return (
              <div key={item.id} onClick={() => !unavailable && setSelected(item)}
                aria-disabled={unavailable}
                className={`flex flex-col bg-white rounded-2xl talabya-shadow-soft overflow-hidden border border-[var(--border)] transition-all duration-300 animate-fade-up ${unavailable ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:-translate-y-1"}`}
                data-testid={`meal-card-${item.id}`}>
                <div className="relative h-48 w-full overflow-hidden">
                  {item.image
                    ? <img src={item.image} alt={item.name} className={`w-full h-full object-cover ${unavailable ? "grayscale" : ""}`}/>
                    : <div className="w-full h-full bg-[var(--surface-alt)] flex items-center justify-center text-3xl">🍽</div>}
                  <div className="absolute top-3 right-3 bg-white/95 text-primary-brand text-xs font-bold px-2.5 py-1 rounded-full">{item.category}</div>
                  {unavailable && (
                    <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
                      <div className="bg-red-600 text-white text-sm font-black px-4 py-1.5 rounded-full shadow-xl" style={{fontFamily:'Cairo'}} data-testid={`unavailable-${item.id}`}>
                        {shopClosed ? "المحل مغلق" : "غير متوفر الآن"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col gap-2 flex-1">
                  <h3 className="font-bold text-lg" style={{fontFamily:'Cairo'}}>{item.name}</h3>
                  <p className="text-sm text-[var(--text-secondary)] line-clamp-2 flex-1">{item.description}</p>
                  <div className="flex justify-between items-center mt-2">
                    <div className="text-primary-brand font-black text-xl" style={{fontFamily:'Cairo'}}>{item.price} <span className="text-xs font-bold">د.ل</span></div>
                    {unavailable ? (
                      <button disabled className="px-4 py-2 bg-[var(--surface-alt)] text-[var(--text-secondary)] rounded-xl font-bold text-sm cursor-not-allowed" data-testid={`add-${item.id}`}>
                        {shopClosed ? "مغلق" : "غير متوفر"}
                      </button>
                    ) : (
                      <button className="px-4 py-2 bg-primary-light text-primary-brand hover:bg-primary-brand hover:text-white rounded-xl font-bold text-sm transition" data-testid={`add-${item.id}`}>+ إضافة</button>
                    )}
                  </div>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>

      {/* Floating cart bar */}
      {cart.count > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-auto z-30 animate-fade-up">
          <button onClick={() => nav("/cart")}
            className="w-full sm:w-auto bg-[var(--text-primary)] text-white px-5 py-4 rounded-2xl talabya-shadow-hover flex items-center justify-between gap-4 hover:bg-primary-brand transition"
            data-testid="floating-cart">
            <span className="font-bold flex items-center gap-2">
              <span className="bg-primary-brand text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-black">{cart.count}</span>
              عرض السلة
            </span>
            <span className="font-black" style={{fontFamily:'Cairo'}}>{cart.subtotal.toFixed(2)} د.ل</span>
          </button>
        </div>
      )}

      {selected && <MealModal item={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}
